import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, TabContent, TabPane } from "reactstrap";
import { Storage } from "aws-amplify";
import { StorageImage } from '@aws-amplify/ui-react-storage';

// -------------------- ENUMS --------------------
const plantGrowingCycleOptions = ["annual", "biennial", "perennial"];

const plantSizeOptions = [
    "tiny", "small", "medium", "large", "shrub", "small tree", "medium tree", "large tree",
];

const typeOfHarvestOptions = [
    "root", "fruit", "bark", "berries", "seeds", "flowers", "leaves",
];

const climateAllowed = [
    "Subtropical", "Tropical", "Temperate", "Mediterranean", "Cold Desert",
    "Hot Desert", "Cool Temperate",
];

const foodForestLayerAllowed = [
    "Canopy", "Subcanopy", "Shrub/Bush", "Herbaceous", "Ground Cover",
    "Root Crop", "Vining",
];

const guildFunctionAllowed = [
    "Erosion Control", "Windbreak / Privacy", "Pollinator / Insectary",
    "Food / Fiber", "Habitat", "Medicinal", "Dynamic Accumulator", "Nitrogen Fixer",
];

const usdaZoneOptions = [
    { value: "", text: "" },
    { value: "1", text: "1a-1b" },
    { value: "2", text: "2a-2b" },
    { value: "3", text: "3a-3b" },
    { value: "4", text: "4a-4b" },
    { value: "5", text: "5a-5b" },
    { value: "6", text: "6a-6b" },
    { value: "7", text: "7a-7b" },
    { value: "8", text: "8a-8b" },
    { value: "9", text: "9a-9b" },
    { value: "10", text: "10a-10b" },
    { value: "11", text: "11a-11b" },
    { value: "12", text: "12a-12b" },
];

// -------------------- IMAGE RESIZE + UPLOAD HELPERS --------------------
async function loadImageFromFile(file) {
    const img = new Image();
    const url = URL.createObjectURL(file);
    try {
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
        });
        return img;
    } finally {
        URL.revokeObjectURL(url);
    }
}

function drawContain(ctx, img, targetW, targetH) {
    const scale = Math.min(targetW / img.width, targetH / img.height);
    const drawW = Math.round(img.width * scale);
    const drawH = Math.round(img.height * scale);
    const dx = Math.floor((targetW - drawW) / 2);
    const dy = Math.floor((targetH - drawH) / 2);
    ctx.drawImage(img, dx, dy, drawW, drawH);
}

async function resizePlannerIconToPng(file) {
    const img = await loadImageFromFile(file);
    const canvas = document.createElement("canvas");
    canvas.width = 150;
    canvas.height = 150;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, 150, 150);
    drawContain(ctx, img, 150, 150);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("Failed to encode planner icon PNG");
    return new File([blob], "planner.png", { type: "image/png" });
}

async function resizeCardIconToJpeg(file, background = "#ffffff") {
    const img = await loadImageFromFile(file);
    const canvas = document.createElement("canvas");
    canvas.width = 324;
    canvas.height = 79;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, 324, 79);
    drawContain(ctx, img, 324, 79);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) throw new Error("Failed to encode card icon JPG");
    return new File([blob], "card.jpg", { type: "image/jpeg" });
}

function customPlantIconKeys({ username, plantId }) {
    const base = `${username}/customplant/photo/${plantId}`;
    return {
        plannerKey: `${base}/planner.png`,
        cardKey: `${base}/card.jpg`,
    };
}

// -------------------- REUSABLE COMPONENTS --------------------
function Field({ label, children }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <Label style={{ fontWeight: 500, marginBottom: 4, display: "block", fontSize: "0.875rem" }}>{label}</Label>
            {children}
        </div>
    );
}

function SectionHeader({ children }) {
    return (
        <div style={{
            fontSize: "0.75rem", fontWeight: 600, color: "#7F8F7B",
            textTransform: "uppercase", letterSpacing: "0.06em",
            borderBottom: "1px solid #dde5d7", paddingBottom: 4, marginBottom: 14, marginTop: 20,
        }}>
            {children}
        </div>
    );
}

function CheckboxGroup({ name, options, values = [], onChange }) {
    function toggle(option) {
        const current = Array.isArray(values) ? values : [];
        const next = current.includes(option)
            ? current.filter((v) => v !== option)
            : [...current, option];
        onChange(name, next);
    }

    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
            {options.map((option) => (
                <FormGroup check key={option} style={{ marginBottom: 0 }}>
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={Array.isArray(values) && values.includes(option)}
                            onChange={() => toggle(option)}
                        />{" "}
                        {option}
                    </Label>
                </FormGroup>
            ))}
        </div>
    );
}

// -------------------- MAIN MODAL --------------------
export default function CustomPlantModal({
    isOpen,
    toggle,
    username,
    mode,
    initialPlant,
    onSave,
    onUpdate,
    onDelete,
}) {
    const [plant, setPlant] = useState(initialPlant || {});
    const [designerIconFile, setDesignerIconFile] = useState(null);
    const [cardIconFile, setCardIconFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("info");

    const isCreate = mode === "create";

    useEffect(() => {
        setPlant(initialPlant || {});
        setDesignerIconFile(null);
        setCardIconFile(null);
        setError("");
        setSaving(false);
        setActiveTab("info");
    }, [initialPlant, isOpen]);

    function setField(key, value) {
        setPlant((p) => ({ ...p, [key]: value }));
    }

    function validate() {
        const errs = [];
        if (!(plant?.name?.trim() || plant?.species?.trim())) {
            errs.push({ tab: "info", msg: "Name is required." });
        }
        if (plant.season_rotation === "annual" && !String(plant.daysToHarvest || "").trim()) {
            errs.push({ tab: "info", msg: "Days to harvest is required for annuals." });
        }
        if (!plant.designerIconKey && !designerIconFile) {
            errs.push({ tab: "icons", msg: "Planner icon is required." });
        }
        if (!plant.cardIconKey && !cardIconFile) {
            errs.push({ tab: "icons", msg: "Card icon is required." });
        }
        return errs;
    }

    async function uploadIconsIfNeeded() {
        const plantId = plant.id;
        const { plannerKey, cardKey } = customPlantIconKeys({ username, plantId });

        let designerIconKey = plant.designerIconKey || "";
        let cardIconKey = plant.cardIconKey || "";

        if (designerIconFile) {
            const plannerOut = await resizePlannerIconToPng(designerIconFile);
            await Storage.put(plannerKey, plannerOut, {
                level: "public",
                contentType: "image/png",
                cacheControl: "max-age=31536000, immutable",
            });
            designerIconKey = plannerKey;
        }

        if (cardIconFile) {
            const cardOut = await resizeCardIconToJpeg(cardIconFile, "#ffffff");
            await Storage.put(cardKey, cardOut, {
                level: "public",
                contentType: "image/jpeg",
                cacheControl: "max-age=31536000, immutable",
            });
            cardIconKey = cardKey;
        }

        return { designerIconKey, cardIconKey };
    }

    async function handleSave() {
        setError("");
        const errs = validate();
        if (errs.length > 0) {
            setActiveTab(errs[0].tab);
            setError(errs.map((e) => e.msg).join(" "));
            return;
        }

        setSaving(true);
        try {
            const { designerIconKey, cardIconKey } = await uploadIconsIfNeeded();
            // keep species in sync with name for backwards compatibility
            const toPersist = { ...plant, username, designerIconKey, cardIconKey, species: plant.name || plant.species };

            if (isCreate) await onSave(toPersist);
            else await onUpdate(toPersist);

            toggle();
        } catch (e) {
            console.error(e);
            setError(e?.message || "Failed to save custom plant.");
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!plant?.id) return;
        setSaving(true);
        setError("");
        try {
            await onDelete(plant.id);
            toggle();
        } catch (e) {
            console.error(e);
            setError(e?.message || "Failed to delete custom plant.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal className="modal-dialog-scrollable" isOpen={isOpen} toggle={toggle} size="md">
            <ModalHeader toggle={toggle}>
                {isCreate ? "Add custom plant" : `Edit: ${plant.name || plant.species || ""}`}
            </ModalHeader>

            <ModalBody style={{ padding: 0 }}>
                <div style={{ fontSize: "0.78rem", color: "#6c757d", padding: "8px 16px 4px", borderBottom: "1px solid #eee" }}>
                    Required: name, both icons{plant.season_rotation === "annual" ? ", days to harvest (annual)" : ""}
                </div>

                {error ? <div style={{ color: "crimson", padding: "6px 16px 0", fontSize: "0.875rem" }}>{error}</div> : null}

                <div style={{ backgroundColor: "#D9DEE5", borderBottom: "2px solid #b8c0cc", display: "flex" }}>
                    {[
                        { id: "info", label: "Info" },
                        { id: "permaculture", label: "Permaculture" },
                        { id: "icons", label: "Icons" },
                    ].map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            style={{
                                background: "none", border: "none", padding: "8px 16px",
                                marginBottom: "-2px",
                                borderBottom: activeTab === id ? "2px solid #495057" : "2px solid transparent",
                                color: activeTab === id ? "#212529" : "#6c757d",
                                fontWeight: activeTab === id ? 600 : 400,
                                cursor: "pointer",
                            }}
                            onClick={() => setActiveTab(id)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <TabContent activeTab={activeTab} style={{ paddingLeft: "1.25rem", paddingRight: "1.25rem", paddingTop: "20px", paddingBottom: "20px" }}>
                    <TabPane tabId="info">
                        <SectionHeader>Identity</SectionHeader>
                        <Field label="Name *">
                            <Input
                                value={plant.name || ""}
                                onChange={(e) => setField("name", e.target.value)}
                                placeholder="e.g. Ground cherry"
                            />
                        </Field>
                        <Field label="Latin name">
                            <Input value={plant.latinName || ""} onChange={(e) => setField("latinName", e.target.value)} />
                        </Field>
                        <Field label="Plant family">
                            <Input value={plant.plantFamily || ""} onChange={(e) => setField("plantFamily", e.target.value)} />
                        </Field>

                        <SectionHeader>Basic properties</SectionHeader>
                        <Field label="Growing cycle">
                            <Input type="select" value={plant.season_rotation || ""} onChange={(e) => setField("season_rotation", e.target.value)}>
                                <option value=""></option>
                                {plantGrowingCycleOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                            </Input>
                        </Field>
                        <Field label="Size">
                            <Input type="select" value={plant.size || ""} onChange={(e) => setField("size", e.target.value)}>
                                <option value=""></option>
                                {plantSizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                            </Input>
                        </Field>
                        <Field label="Type of harvest">
                            <Input type="select" value={plant.typeOfHarvest || ""} onChange={(e) => setField("typeOfHarvest", e.target.value)}>
                                <option value=""></option>
                                {typeOfHarvestOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                            </Input>
                        </Field>
                        <Field label="Germination time">
                            <Input value={plant.germinationTime || ""} onChange={(e) => setField("germinationTime", e.target.value)} placeholder="e.g. 7-14 days" />
                        </Field>
                        <Field label="Days to harvest">
                            <Input value={plant.daysToHarvest || ""} onChange={(e) => setField("daysToHarvest", e.target.value)} placeholder="e.g. 90 or N/A" />
                        </Field>
                        <Field label="Spacing between plants (in)">
                            <Input type="number" value={plant.spacing_between_plants || ""} onChange={(e) => setField("spacing_between_plants", Number(e.target.value))} />
                        </Field>
                        <Field label="Yield per plant (oz)">
                            <Input type="number" value={plant.yieldPerPlant || ""} onChange={(e) => setField("yieldPerPlant", Number(e.target.value))} />
                        </Field>
                        <Field label="Min temperature (°F)">
                            <Input type="number" value={plant.minimumTemperature || ""} onChange={(e) => setField("minimumTemperature", Number(e.target.value))} />
                        </Field>
                        <Field label="Max temperature (°F)">
                            <Input type="number" value={plant.maximumTemperature || ""} onChange={(e) => setField("maximumTemperature", Number(e.target.value))} />
                        </Field>
                    </TabPane>

                    <TabPane tabId="permaculture">
                        <Field label="Climate">
                            <CheckboxGroup name="climate" options={climateAllowed} values={plant.climate} onChange={setField} />
                        </Field>
                        <Field label="Food forest layer">
                            <CheckboxGroup name="foodForestLayer" options={foodForestLayerAllowed} values={plant.foodForestLayer} onChange={setField} />
                        </Field>
                        <Field label="Guild function">
                            <CheckboxGroup name="guildFunction" options={guildFunctionAllowed} values={plant.guildFunction} onChange={setField} />
                        </Field>
                        <Field label="USDA min zone">
                            <Input type="select" value={plant.USDAMin != null ? String(plant.USDAMin) : ""} onChange={(e) => setField("USDAMin", e.target.value === "" ? "" : Number(e.target.value))}>
                                {usdaZoneOptions.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                            </Input>
                        </Field>
                        <Field label="USDA max zone">
                            <Input type="select" value={plant.USDAMax != null ? String(plant.USDAMax) : ""} onChange={(e) => setField("USDAMax", e.target.value === "" ? "" : Number(e.target.value))}>
                                {usdaZoneOptions.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                            </Input>
                        </Field>
                    </TabPane>

                    <TabPane tabId="icons">
                        <Field label="Planner icon (150×150 PNG)">
                            <Input type="file" accept="image/*" onChange={(e) => setDesignerIconFile(e.target.files?.[0] || null)} />
                            <div style={{ marginTop: 8 }}>
                                {designerIconFile ? (
                                    <div style={{ fontSize: 12, opacity: 0.8 }}>Selected: {designerIconFile.name}</div>
                                ) : plant.designerIconKey ? (
                                    <StorageImage imgKey={plant.designerIconKey} style={{ width: 150, height: 150, border: "1px solid #ddd", borderRadius: 4 }} />
                                ) : (
                                    <div style={{ fontSize: 12, opacity: 0.7 }}>No planner icon yet.</div>
                                )}
                            </div>
                        </Field>
                        <Field label="Card icon (324×79)">
                            <Input type="file" accept="image/*" onChange={(e) => setCardIconFile(e.target.files?.[0] || null)} />
                            <div style={{ marginTop: 8 }}>
                                {cardIconFile ? (
                                    <div style={{ fontSize: 12, opacity: 0.8 }}>Selected: {cardIconFile.name}</div>
                                ) : plant.cardIconKey ? (
                                    <StorageImage imgKey={plant.cardIconKey} style={{ width: 324, height: 79, border: "1px solid #ddd", borderRadius: 4 }} />
                                ) : (
                                    <div style={{ fontSize: 12, opacity: 0.7 }}>No card icon yet.</div>
                                )}
                            </div>
                        </Field>
                    </TabPane>
                </TabContent>
            </ModalBody>

            <ModalFooter>
                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }} disabled={saving} onClick={() => { setError(""); toggle(); }}>
                    Cancel
                </button>

                {!isCreate && (
                    <button type="button" className="btn btn-sm" style={{ backgroundColor: "#B93535", borderColor: "#B93535", color: "#fff" }} disabled={saving} onClick={handleDelete}>
                        Delete
                    </button>
                )}

                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} disabled={saving} onClick={handleSave}>
                    {saving ? "Saving…" : "Save"}
                </button>
            </ModalFooter>
        </Modal>
    );
}
